import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { formatIDR, formatIDRShort, formatDate } from "../lib/format";
import { Wallet, TrendingUp, TrendingDown, Receipt, Building2, CreditCard, Activity } from "lucide-react";

const KPI = ({ icon: Icon, title, value, tone = "default", testId }) => (
  <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8C9096] font-semibold">{title}</div>
        <div className={`mt-2 font-display text-xl font-bold ${tone === "danger" ? "text-rose-700" : tone === "success" ? "text-emerald-700" : "text-[#1E2022]"}`}>{value}</div>
      </div>
      <div className="w-9 h-9 rounded-md accent-surface flex items-center justify-center"><Icon className="w-4 h-4 text-brand" /></div>
    </div>
  </Card>
);

export default function FinanceDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finance/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-sm text-[#8C9096]">Memuat data keuangan...</div>;

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div>
        <h1 className="font-display text-2xl font-bold">Dasbor Keuangan Manajemen</h1>
        <p className="text-sm text-[#5C6066] mt-1">Ringkasan operasional keuangan Dessy Wedding Group. Hanya Owner & Admin.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Receipt} title="Total Kontrak" value={formatIDRShort(d.total_contract)} testId="fin-contract" />
        <KPI icon={TrendingUp} title="Diterima dari Klien" value={formatIDRShort(d.client_received)} tone="success" testId="fin-received" />
        <KPI icon={Wallet} title="Piutang Klien" value={formatIDRShort(d.client_receivable)} tone="danger" testId="fin-receivable" />
        <KPI icon={Activity} title="Kontribusi Estimasi" value={formatIDRShort(d.estimated_contribution)} testId="fin-contribution" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Building2} title="Vendor Payable" value={formatIDRShort(d.vendor_total)} testId="fin-vendor-total" />
        <KPI icon={Building2} title="Vendor Terbayar" value={formatIDRShort(d.vendor_paid)} tone="success" testId="fin-vendor-paid" />
        <KPI icon={Building2} title="Vendor Belum Bayar" value={formatIDRShort(d.vendor_outstanding)} tone="danger" testId="fin-vendor-outstanding" />
        <KPI icon={CreditCard} title="Fee Crew Belum Bayar" value={formatIDRShort(d.wage_outstanding)} tone="danger" testId="fin-wage-outstanding" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none">
          <div className="text-xs uppercase tracking-wider text-[#8C9096] font-semibold mb-3">Arus Kas Operasional</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Kas Masuk</span><span className="font-mono">{formatIDR(d.cash_in)}</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2"><TrendingDown className="w-4 h-4 text-rose-600" />Kas Keluar</span><span className="font-mono">{formatIDR(d.cash_out)}</span></div>
            <div className="flex justify-between pt-2 border-t border-[#F0EBE0] font-semibold"><span>Net Kas</span><span className="font-mono">{formatIDR(d.cash_in - d.cash_out)}</span></div>
          </div>
          <div className="mt-4 text-[11px] text-[#8C9096]">*Angka manajemen, bukan laporan akuntansi formal.</div>
        </Card>

        <Card className="border border-[#E5E0D8] bg-white shadow-none">
          <div className="px-5 py-4 border-b border-[#E5E0D8]"><h2 className="font-display font-semibold">Pembayaran Terbaru</h2></div>
          <div className="divide-y divide-[#F0EBE0] max-h-[300px] overflow-y-auto">
            {(d.recent_payments || []).length === 0 && <div className="p-6 text-sm text-center text-[#8C9096]">Belum ada pembayaran.</div>}
            {(d.recent_payments || []).map((p) => (
              <div key={p.id} className="px-5 py-3 flex justify-between text-sm">
                <div>
                  <div>{p.payment_type}</div>
                  <div className="text-xs text-[#8C9096]">{formatDate(p.payment_date)} — {p.method}</div>
                </div>
                <div className="font-mono font-semibold">{formatIDR(p.amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
